import React, { useState, useEffect, useRef } from 'react'
import { enqueueInitialization } from './globalQueue' // Assuming this path is correct

export default function SafeModelViewer({ visible, src, iosSrc, skyboxImage, skyboxHeight, style, ...rest }) {
  const ref = useRef()
  // Controls rendering the <model-viewer> tag
  const [mounted, setMounted] = useState(false)
  // Tracks if the component has started or is currently in the queue/initialization phases
  const [isInitializing, setIsInitializing] = useState(false)

  // --- Initialization and Cleanup ---
  useEffect(() => {
    // 1. SCROLL-OUT CLEANUP (UNLOAD)
    if (!visible) {
      // Reset flags so the next time visible is true, it queues up again.
      setIsInitializing(false)
      setMounted(false) // Ensures the component renders the placeholder div next time
      return
    }

    // 2. CHECK & PREVENT RE-QUEUEING
    if (isInitializing) {
      return
    }

    // Flag that we are starting initialization process
    setIsInitializing(true)
    let isCancelled = false // Safety flag for when the component unmounts mid-queue

    enqueueInitialization(async () => {
      // If component unmounted while waiting in the queue, bail out
      if (isCancelled) return

      // Phase 1: mount model-viewer placeholder
      setMounted(true)

      // Phase 2: Wait a tick to let the element attach to the DOM
      await new Promise((resolve) => setTimeout(resolve, 50))
      if (isCancelled) return

      // Phase 3: Now enable GPU features one-by-one synchronously
      if (ref.current) {
        // NOTE: Attributes like src trigger the heavy WebGL context allocation and loading
        ref.current.setAttribute('src', src)
        ref.current.setAttribute('ios-src', iosSrc)
        ref.current.setAttribute('camera-controls', '')
        ref.current.setAttribute('ar', '')
        ref.current.setAttribute('touch-action', 'pan-y')

        if (skyboxImage) ref.current.setAttribute('skybox-image', skyboxImage)
        if (skyboxHeight) ref.current.setAttribute('skybox-height', skyboxHeight)
        ref.current.setAttribute('shadow-intensity', '1.5')
      }

      // Phase 4: Give GPU time to breathe before the next item in the queue starts
      await new Promise((resolve) => setTimeout(resolve, 200))

      // We are done initializing the component
      setIsInitializing(false)
    })

    // CLEANUP FUNCTION: Runs when component receives visible=false or unmounts entirely
    return () => {
      isCancelled = true // Mark current running/waiting task as invalid
      setIsInitializing(false) // Allow the component to re-queue next time it becomes visible
      // The parent component (CeramicItem) handles the final unmount by removing us from the DOM.
    }
  }, [visible]) // Dependency array must include 'visible'

  // --- Render Logic ---
  if (!mounted) {
    // Placeholder div when not mounted (before queue runs or after unmount)
    return <div style={{ ...style, background: '#f7f7f7' }} />
  }

  // Render the inert model-viewer instance, which the queue will then progressively configure
  return <model-viewer ref={ref} style={style} {...rest}></model-viewer>
}
